import React from 'react';
import { Box, Typography, Grid2, Collapse, Alert, AlertTitle } from '@mui/material';
import { AccessTime, Person, DateRange, Money, CheckCircle, CreditCardOff } from '@mui/icons-material';
import { BookingConfirmation } from '../../types';
import { formatDate, formatTimeRange } from '../../time/timeUtils';
import { formatPhoneNumber } from '../homepage/Contact';

interface BookingConfirmationDetailsProps {
  bookingConfirmation: BookingConfirmation;
  paid: boolean;
  attemptedCardPayment: boolean;
  isInternal?: boolean;
}

const BookingConfirmationDetails: React.FC<BookingConfirmationDetailsProps> = ({
  bookingConfirmation,
  attemptedCardPayment,
  paid,
  isInternal = false,
}) => {
  const subtotal = typeof bookingConfirmation.paymentInfo?.serviceAmountCents === 'number'
    ? bookingConfirmation.paymentInfo.serviceAmountCents / 100
    : bookingConfirmation.billingAmount;
  const tip = typeof bookingConfirmation.paymentInfo?.tipAmountCents === 'number'
    ? bookingConfirmation.paymentInfo.tipAmountCents / 100
    : 0;
  const total = typeof bookingConfirmation.paymentInfo?.amountCents === 'number'
    ? bookingConfirmation.paymentInfo.amountCents / 100
    : subtotal + tip;
  const hasTip = tip > 0;

  return (
    <Box sx={{ mt: 4, padding: 3 }}>
      {/* Green Checkbox Icon */}
      <Collapse in={attemptedCardPayment && !paid} appear>
        <Alert
          severity="warning"
          icon={<CreditCardOff />}
          sx={{
            mt: 2,
            borderRadius: 2,
            boxShadow: 1,
            alignItems: 'flex-start',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Error Processing Payment!</AlertTitle>
          Please pay in person at the time of your appointment.
        </Alert>
      </Collapse>
      <Box sx={{ textAlign: 'center', mb: 2, mt: 2 }}>
        <CheckCircle sx={{ color: 'green', fontSize: '2.6rem' }} />
      </Box>
      <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: '600' }}>
        Booking Confirmed
      </Typography>
      {!isInternal && (
        <Typography variant="h6" align="center" gutterBottom>
          See you soon!
        </Typography>
      )}
      <hr
        style={{
          border: "none",
          height: "2px",
          backgroundColor: "rgba(0,0,0,0.2)",
          boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
          marginTop: "20px",
          marginBottom: "20px",
        }}
      />
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Typography variant="h5" align="center">
            {bookingConfirmation.appointmentTitle}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography variant="body1" fontWeight={500}>
            Confirmation ID: {bookingConfirmation.id.substring(0, 6)}
          </Typography>
        </Grid2>
        {(bookingConfirmation.customerFirstName || bookingConfirmation.customerLastName || bookingConfirmation.customerPhoneNumber) && (
          <Grid2 size={12}>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              {[bookingConfirmation.customerFirstName, bookingConfirmation.customerLastName]
                .filter(Boolean)
                .join(' ')}
              {bookingConfirmation.customerPhoneNumber && (
                <>
                  {', '} {formatPhoneNumber(bookingConfirmation.customerPhoneNumber)}
                </>
              )}
            </Typography>
          </Grid2>
        )}
        <Grid2 size={12}>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 1, color: 'lightblue' }} />
            {bookingConfirmation.employee}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
            <DateRange sx={{ mr: 1, color: 'crimson' }} />
            {formatDate(bookingConfirmation.appointmentDate)}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTime sx={{ mr: 1, color: 'gray' }} />
            {formatTimeRange(bookingConfirmation.appointmentTime, bookingConfirmation.appointmentDuration)}
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
            <Money sx={{ mr: 1, color: 'green' }} />
            Subtotal: ${subtotal.toFixed(2)}
          </Typography>
        </Grid2>
        {hasTip && (
          <Grid2 size={12}>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              <Money sx={{ mr: 1, color: 'green' }} />
              Tip: ${tip.toFixed(2)}
            </Typography>
          </Grid2>
        )}
        <Grid2 size={12}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
            <Money sx={{ mr: 1, color: 'green' }} />
            Total: ${total.toFixed(2)}
          </Typography>
        </Grid2>
        {bookingConfirmation.notes && (
          <Grid2 size={12}>
            <Typography variant="body1">
              Notes: {bookingConfirmation.notes}
            </Typography>
          </Grid2>
        )}
      </Grid2>

    </Box>
  );
};

export default BookingConfirmationDetails;
